import { Injectable, Inject } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, pipe, combineLatest, EMPTY } from 'rxjs';
import { catchError, switchMap, take, filter } from 'rxjs/operators';
import {
  FaresActions,
  TicketProduct,
} from '@modules/ticket-fares-store';
import { LOGGER } from '@modules/core';
import { LoadingDetails, LoggingService } from '@nx-ng/types';
import { DetermineBasketUpdateService, CheckoutService } from '../../services';
import { CheckoutStoreFacade } from '../../facade';
import { CheckoutActions, VoucherCodeActions } from '../../actions';
import { ApiError, Basket, BasketItem } from '../../client/src';
import { BasketDeleted, isBasketDeleted } from '../../types';
import { DiscountService } from '@modules/discount-store/services';
import { VoucherCodeApplyResponse } from '@modules/discount-store/client/src';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { MsalPolicyService } from '@modules/core/services/msalPolicy.service';

@Injectable()
export class BasketEffects {
  loadBasketOnB2CLogin$ = createEffect(() =>
    // Reload the basket when the user logs in, and has a basket
    // Ensures that the basket is associated to them and loaded appropriately
    this.msalPolicyService.token$.pipe((token) =>
    combineLatest([token, this.checkoutStoreFacade.selectBasketId$]).pipe(
      switchMap(([token, basketId]) => {
          if (token && basketId) {
            return [CheckoutActions.checkoutAPI_loadBasket({ basketId: basketId })];
          }

          return []
        })
      )
    )
  )

  loadBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.checkoutAPI_loadBasket),
      switchMap((action: { basketId: string } & Action) => {
        return combineLatest([of(action), this.msalPolicyService.token$]).pipe(
          take(1),
          switchMap(
            ([action, token]: [{ basketId: string } & Action, string | undefined]) => {
              const { basketId } = action;

              const associateCustomerWithBasketIfLoggedIn = token ? this.discountService.associateCustomerWithBasket(basketId, token) : EMPTY
              return associateCustomerWithBasketIfLoggedIn.pipe(() => {
                return this.checkoutService.loadBasketById(basketId).pipe(
                  switchMap((basket?: Basket) => {
                    if (!basket) {
                      return of(
                        CheckoutActions.checkoutAPI_loadBasketFailure({
                          error: 'Empty basket response received',
                          retryAction: action,
                        })
                      );
                    }

                    const rejectedVoucherCodeActions: Action[] = [];
                    if (basket.discount && basket.discount.discountRejectedReason) {
                      // rejected the discount after quantity update - remove the code but preserve the error
                      rejectedVoucherCodeActions.push(...[
                        VoucherCodeActions.discountAPI_removeVoucherCode({ basketId: basket.basketId, voucherCode: basket.discount.voucherCode }),
                        VoucherCodeActions.discountAPI_applyVoucherCode({ basketId: basket.basketId, voucherCode: basket.discount.voucherCode }), // show error from a fresh state
                        CheckoutActions.recreateBasketFromsAliasSuccess(), // show the basket so the user can see the voucher updates
                      ]);
                    }

                    return [
                      CheckoutActions.checkoutAPI_loadBasketSuccess({ basket }),
                      ...this.loadFareActionsForBasket(basket),
                      ...rejectedVoucherCodeActions,
                    ];
                  }),
                  catchError((error) => {
                    const apiError: ApiError | string =
                      error?.response?.parsedBody || error;

                    this.logger.logApiError(
                      {
                        apiError,
                      },
                      error
                    );

                    return of(
                      CheckoutActions.checkoutAPI_loadBasketFailure({
                        error: apiError,
                        retryAction: action,
                      })
                    );
                  })
                );
              })
            })
        )
      })
    )
  );

  updateBasketItemQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.updateBasketItemQuantity),
      switchMap((action) => {
        return combineLatest([
          this.determineBasketUpdateService.performBasketUpdate(action.item),
          this.checkoutStoreFacade.selectBasket$,
        ]).pipe(
          take(1),
          switchMap(
            ([basket, oldBasket]: [
              Basket | BasketDeleted | undefined,
              Basket | undefined
            ]) => {
              if (!basket) {
                return of(
                  CheckoutActions.checkoutAPI_updateBasketFailure({
                    error: 'Error updating item in basket',
                    retryAction: action,
                  })
                );
              }

              const gtmActions: Action[] = [];
              gtmActions.push(
                CheckoutActions.sendBasketEventToGtm({
                  basketPriorToEvent: oldBasket,
                  basketItem: action.item,
                })
              );

              if (isBasketDeleted(basket)) {
                return [
                  CheckoutActions.checkoutAPI_removeBasketSuccess(),
                  ...gtmActions,
                ];
              }

              const rejectedVoucherCodeActions: Action[] = [];
              if (basket.discount && basket.discount.discountRejectedReason) {
                // rejected the discount after quantity update - remove the code but preserve the error
                rejectedVoucherCodeActions.push(...[
                  VoucherCodeActions.discountAPI_removeVoucherCode({ basketId: basket.basketId, voucherCode: basket.discount.voucherCode }),
                  VoucherCodeActions.discountAPI_applyVoucherCode({ basketId: basket.basketId, voucherCode: basket.discount.voucherCode }), // show error from a fresh state
                ]);
              }

              //Send GTM event for basket update

              if (action.showBasketOnSuccess) {
                return [
                  CheckoutActions.checkoutAPI_updateBasketSuccess({ basket }),
                  CheckoutActions.recreateBasketFromsAliasSuccess(),
                  ...rejectedVoucherCodeActions,
                  ...gtmActions,
                ];
              }

              return [
                CheckoutActions.checkoutAPI_updateBasketSuccess({ basket }),
                ...rejectedVoucherCodeActions,
                ...gtmActions,
              ];
            }
          ),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || error;

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );

            return of(
              CheckoutActions.checkoutAPI_updateBasketFailure({
                error: apiError,
                retryAction: action,
              })
            );
          })
        );
      })
    )
  );

  loadTicketByAlias$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.addTicketToBasketUsingAlias),
      pipe(
        switchMap(
          ({
            ticketAlias,
            basketId,
          }: {
            ticketAlias: string;
            basketId?: string;
          }) => {
            const basketActions: Action[] = [];

            if (basketId) {
              basketActions.push(
                CheckoutActions.checkoutAPI_loadBasket({ basketId })
              );
            }

            return [
              FaresActions.loadFareByAlias({
                ticketAlias: ticketAlias,
                basketId,
                quantity: 1,
              }),
              ...basketActions,
            ];
          }
        ),
        catchError(() => {
          return of(CheckoutActions.recreateBasketFromsAliasFailure);
        })
      )
    )
  );

  addTicketToBasketByAlias$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FaresActions.loadFareByAliasSuccess),
      switchMap((action) => {
        if (action.basketId) {
          return combineLatest([
            of(action.fareResponse),
            this.checkoutStoreFacade.selectBasket$.pipe(
              filter((basket?: Basket) => basket?.basketId === action.basketId),
              take(1)
            ),
            of(undefined),
          ]).pipe(take(1));
        }

        return combineLatest([
          of(action.fareResponse),
          this.checkoutStoreFacade.selectBasket$,
          this.checkoutStoreFacade.isCheckoutLoading$.pipe(
            filter((loading: LoadingDetails) => !loading.isLoading)
          ),
        ]).pipe(take(1));
      }),

      switchMap(
        ([fareResponse, basket]: [
          TicketProduct,
          Basket | undefined,
          LoadingDetails | undefined
        ]) => {
          // Do not add ticket product to the basket if it is already in the basket
          if (
            basket &&
            basket.basketItems.some(
              (item) => item.ticketID === fareResponse?.id
            )
          ) {
            return EMPTY;
          }

          return of(
            CheckoutActions.updateBasketItemQuantity({
              item: {
                quantity: 1,
                ticketID: fareResponse.id!,
              },
              showBasketOnSuccess: true,
            })
          );
        }
      )
    )
  );

  applyVoucherCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VoucherCodeActions.discountAPI_applyVoucherCode),
      switchMap((action: { basketId: string; voucherCode: string } & Action) => {
        const { basketId, voucherCode } = action;
        return this.discountService.applyVoucherCode(basketId, voucherCode).pipe(
          switchMap((response: VoucherCodeApplyResponse) => {
            if (response.discount.discountRejectedReason) {
              return of(
                VoucherCodeActions.discountAPI_applyVoucherCodeFailure({
                  voucherCode: response.discount.voucherCode,
                  errorMsg: response.discount.discountRejectedReason || this.i18n.t('basket_voucher_code_unavailable', {
                    ns: 'translation',
                  })
                })
              );
            }
            return [
              VoucherCodeActions.discountAPI_applyVoucherCodeSuccess({ basketId, voucherCode }),
              CheckoutActions.checkoutAPI_loadBasket({ basketId })
            ];
          }),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || error;

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );

            let errorMessage = this.i18n.t('basket_voucher_code_unavailable', { ns: 'translation' });

            if (typeof apiError !== 'string' && apiError.code === 400) {
              // Generic error validation message
              errorMessage = this.i18n.t('basket_voucher_code_invalid', { ns: 'translation' });
            }

            return of(
              VoucherCodeActions.discountAPI_applyVoucherCodeFailure({
                voucherCode: action.voucherCode,
                errorMsg: errorMessage,
                retryAction: action,
              })
            );
          })
        );
      })
    )
  );

  removeVoucherCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VoucherCodeActions.discountAPI_removeVoucherCode),
      switchMap((action: { basketId: string; voucherCode: string } & Action) => {
        const { basketId, voucherCode } = action;

        return combineLatest([
          this.checkoutStoreFacade.selectBasket$,
        ]).pipe(
          take(1),
          switchMap(
            ([basket]: [
              Basket | undefined
            ]) => {
              return this.discountService.removeVoucherCode(basketId).pipe(
                switchMap(() => {
                  if (basket) {
                    return [
                      VoucherCodeActions.discountAPI_removeVoucherCodeSuccess({ basketId }),
                      CheckoutActions.checkoutAPI_updateBasketSuccess({ basket: { ...basket, discount: undefined, toPayBasketTotal: basket.basketTotal } })
                    ];
                  }

                  return []
                }),
                catchError((error) => {
                  const apiError: ApiError | string =
                    error?.response?.parsedBody || error;

                  this.logger.logApiError(
                    {
                      apiError,
                    },
                    error
                  );

                  return of(
                    VoucherCodeActions.discountAPI_removeVoucherCodeFailure({
                      errorMsg: this.i18n.t('basket_voucher_code_unavailable', {
                        ns: 'translation',
                      }),
                      voucherCode,
                      retryAction: action,
                    })
                  );
                })
              );
            }
          )
        )
      })
    )
  );

  associateCustomerWithBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VoucherCodeActions.discountAPI_associateUserWithBasket),
      switchMap((action) =>
        combineLatest([of(action), this.msalPolicyService.token$]).pipe(
          take(1),
          switchMap(
            ([action, token]: [{ basketId: string } & Action, string | undefined]) => {
              if (!token) {
                return EMPTY;
              }

              const { basketId } = action;
              return this.discountService.associateCustomerWithBasket(basketId, token)
                .pipe(switchMap(() => of(VoucherCodeActions.discountAPI_associateUserWithBasketSuccess({ basketId }))),
                catchError((error) => {
                  const apiError: ApiError | string =
                    error?.response?.parsedBody || error;

                  this.logger.logApiError(
                    {
                      apiError,
                    },
                    error
                  );

                  return of(
                    VoucherCodeActions.discountAPI_associateUserWithBasketFailure({
                      errorMsg: this.i18n.t('basket_voucher_code_unavailable', {
                        ns: 'translation',
                      }),
                      retryAction: action,
                    })
                  );
                })
              );
            })
          )
      )
    )
  );

  private loadFareActionsForBasket(basket: Basket) {
    return basket.basketItems.map((item: BasketItem) => {
      return FaresActions.loadFareById({
        ticketID: item.ticketID.toString(),
      });
    });
  }

  constructor(
    private readonly actions$: Actions,
    private determineBasketUpdateService: DetermineBasketUpdateService,
    private checkoutService: CheckoutService,
    private discountService: DiscountService,
    private checkoutStoreFacade: CheckoutStoreFacade,
    private msalPolicyService: MsalPolicyService,
    @Inject(I18NEXT_SERVICE) private i18n: ITranslationService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}
}
